import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Music, X, ArrowLeft, Lightbulb, Wrench } from 'lucide-react';
import CamelotWheel from '../DJTools/CamelotWheel';
import ProTipsTab from './ProTipsTab';
import {
  ChordProgressions,
  ScaleReference,
  FrequencyGuide,
  MidiReference,
  BpmDelayCalculator,
  SampleClearanceChecklist,
  ReleasePrepChecklist,
  SynthPatchNotes,
  ResourceDirectory,
  ToolCard,
  SuggestToolButton,
} from '../DJBooth/DJToolsTab';

const PRODUCER_TOOLS = [
  { id: 'camelot', name: 'Camelot Wheel', icon: '\u{1F3A1}', description: 'Harmonic mixing key wheel', available: true },
  { id: 'landr-mastering', name: 'AI Mastering', icon: '\u{1F39A}\uFE0F', description: 'Professional AI mastering with LANDR', available: true, comingSoon: true },
  { id: 'chord-gen', name: 'Chord Progressions', icon: '\u{1F3BC}', description: 'Generate chord ideas', available: true },
  { id: 'scale-ref', name: 'Scale Reference', icon: '\u{1F3B9}', description: 'Visual scale guide', available: true },
  { id: 'freq-sheet', name: 'Frequency Guide', icon: '\u{1F4CA}', description: 'EQ cheat sheet', available: true },
  { id: 'midi-ref', name: 'MIDI Reference', icon: '\u{1F50C}', description: 'Note to MIDI chart', available: true },
  { id: 'bpm-delay', name: 'BPM-to-Delay', icon: '\u23F1\uFE0F', description: 'Delay & LFO sync times', available: true },
  { id: 'sample-clearance', name: 'Sample Clearance Checklist', icon: '\u{1F4DC}', description: 'Clearance checklist', available: true },
  { id: 'release-prep', name: 'Release Prep', icon: '\u{1F680}', description: 'Pre-release checklist', available: true },
  { id: 'patch-notes', name: 'Synth Patch Notes', icon: '\u{1F39B}\uFE0F', description: 'Document your patches', available: true },
  { id: 'producer-resources', name: 'Producer Resources', icon: '\u{1F9ED}', description: 'DAWs, plugins, samples & education', available: true },
];

const CATEGORY = {
  id: 'producers',
  title: "PRODUCER's",
  icon: Music,
  description: 'Production tools and resources',
  color: '#ff00ff',
  gradient: 'linear-gradient(135deg, #ff00ff, #cc00ff)',
  tools: PRODUCER_TOOLS,
};

const ProducerToolsPanel = () => {
  const [activeView, setActiveView] = useState('tools');
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);

  const handleKeySelect = (camelotKey) => {
    setSelectedKey(camelotKey);
  };

  const renderToolContent = () => {
    if (!selectedTool) return null;

    if (selectedTool.id === 'camelot') {
      return (
        <div style={{ padding: '1rem' }}>
          <CamelotWheel onKeySelect={handleKeySelect} selectedKey={selectedKey} showLegend={true} size={380} />
        </div>
      );
    }
    if (selectedTool.id === 'chord-gen') return <ChordProgressions />;
    if (selectedTool.id === 'scale-ref') return <ScaleReference />;
    if (selectedTool.id === 'freq-sheet') return <FrequencyGuide />;
    if (selectedTool.id === 'midi-ref') return <MidiReference />;
    if (selectedTool.id === 'bpm-delay') return <BpmDelayCalculator />;
    if (selectedTool.id === 'sample-clearance') return <SampleClearanceChecklist />;
    if (selectedTool.id === 'release-prep') return <ReleasePrepChecklist />;
    if (selectedTool.id === 'patch-notes') return <SynthPatchNotes />;
    if (selectedTool.id === 'producer-resources') return <ResourceDirectory category="producer" />;

    if (selectedTool.id === 'landr-mastering') {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem',
          background: 'linear-gradient(180deg, rgba(255, 0, 255, 0.1) 0%, rgba(0, 0, 0, 0) 100%)', borderRadius: '16px'
        }}>
          <div style={{ position: 'relative', marginBottom: '2rem', textAlign: 'center' }}>
            <div style={{ position: 'absolute', inset: '-20px', background: 'radial-gradient(circle, rgba(255, 0, 255, 0.3) 0%, transparent 70%)', filter: 'blur(30px)', animation: 'landrPulse 3s ease-in-out infinite' }} />
            <div style={{ position: 'relative', fontSize: '80px', marginBottom: '1rem' }}>{'\u{1F39A}\uFE0F'}</div>
            <h2 style={{ fontSize: '2rem', fontWeight: '800', margin: '0 0 0.5rem 0', background: 'linear-gradient(135deg, #ff00ff, #00ffff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              AI Mastering with LANDR
            </h2>
            <span style={{ display: 'inline-block', padding: '8px 20px', background: 'linear-gradient(135deg, rgba(255, 0, 255, 0.3), rgba(0, 255, 255, 0.3))', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '25px', fontSize: '0.75rem', fontWeight: '700', color: '#fff', textTransform: 'uppercase', letterSpacing: '3px' }}>
              Coming Soon
            </span>
          </div>
          <p style={{ fontSize: '1.1rem', color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', maxWidth: '600px', marginBottom: '2rem', lineHeight: '1.6' }}>
            Get your tracks release-ready with professional AI-powered mastering. LANDR's industry-leading technology analyzes your music and delivers studio-quality masters in minutes.
          </p>
          <a href="https://www.landr.com/mastering" target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '14px 32px',
            background: 'linear-gradient(135deg, #ff00ff, #cc00ff)', border: 'none', borderRadius: '30px',
            color: '#fff', fontSize: '1rem', fontWeight: '700', textDecoration: 'none', cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(255, 0, 255, 0.4)'
          }}>
            Try LANDR Mastering &rarr;
          </a>
          <style>{`
            @keyframes landrPulse { 0%, 100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.1); } }
          `}</style>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '64px', marginBottom: '20px', filter: 'grayscale(0.5)' }}>{selectedTool.icon}</div>
        <h3 style={{ fontSize: '24px', margin: '0 0 12px 0', color: '#fff' }}>{selectedTool.name}</h3>
        <span style={{ display: 'inline-block', padding: '6px 16px', background: 'linear-gradient(135deg, #ff00ff40, rgba(0,0,0,0.2))', border: '1px solid #ff00ff60', borderRadius: '20px', fontSize: '12px', fontWeight: '600', color: '#ff00ff', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '16px' }}>Coming Soon</span>
        <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px' }}>{selectedTool.description}</p>
      </div>
    );
  };

  // View Toggle
  const ViewToggle = () => (
    <div style={{ display: 'flex', gap: '6px', marginBottom: '1.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '4px' }}>
      <button onClick={() => { setActiveView('tips'); setSelectedTool(null); }} data-testid="producer-view-tips" style={{
        flex: 1, padding: '12px', fontSize: '13px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', border: 'none',
        background: activeView === 'tips' ? 'linear-gradient(135deg, rgba(255,170,0,0.2), rgba(255,136,0,0.15))' : 'rgba(255,255,255,0.04)',
        color: activeView === 'tips' ? '#ffaa00' : '#888', transition: 'all 0.2s',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <Lightbulb size={16} /> Pro Tips
        </span>
      </button>
      <button onClick={() => setActiveView('tools')} data-testid="producer-view-tools" style={{
        flex: 1, padding: '12px', fontSize: '13px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', border: 'none',
        background: activeView === 'tools' ? 'linear-gradient(135deg, rgba(255,0,255,0.15), rgba(204,0,255,0.1))' : 'rgba(255,255,255,0.04)',
        color: activeView === 'tools' ? '#ff00ff' : '#888', transition: 'all 0.2s',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <Wrench size={16} /> Producer Tools
        </span>
      </button>
    </div>
  );

  if (activeView === 'tips') {
    return (
      <div style={{ padding: '2rem', color: '#fff' }}>
        <ViewToggle />
        <ProTipsTab />
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', color: '#fff' }}>
      <ViewToggle />

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '2rem' }}>
        <div style={{
          width: '50px', height: '50px', borderRadius: '12px',
          background: CATEGORY.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Music size={24} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff', margin: '0 0 4px 0' }}>Producer Tools</h2>
          <p style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>Production tools and resources</p>
        </div>
        <SuggestToolButton category="producer" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
        {PRODUCER_TOOLS.map(tool => (
          <ToolCard key={tool.id} tool={tool} category={CATEGORY} onClick={() => setSelectedTool(tool)} />
        ))}
      </div>

      {selectedTool && ReactDOM.createPortal(
        <div
          data-testid="producer-tool-panel-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setSelectedTool(null); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            animation: 'toolPanelFadeIn 0.25s ease-out',
          }}
        >
          <div
            data-testid="producer-tool-panel-content"
            style={{
              position: 'relative', maxHeight: '82vh',
              background: 'rgba(12,12,18,0.98)', borderRadius: '20px 20px 0 0',
              overflow: 'hidden', animation: 'toolPanelSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, #ff00ff, #cc00ff, #ff00ff, #cc00ff)', backgroundSize: '300% 100%', animation: 'gradientBorderFlow 3s linear infinite' }} />
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
              <div style={{ width: '36px', height: '4px', borderRadius: '4px', background: 'rgba(255,255,255,0.15)' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 20px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ fontSize: '28px' }}>{selectedTool.icon}</span>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, color: '#fff', fontSize: '1.1rem', fontWeight: 700 }}>{selectedTool.name}</h3>
                <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>{selectedTool.description}</p>
              </div>
              <button
                onClick={() => setSelectedTool(null)}
                data-testid="producer-tool-panel-close"
                style={{
                  width: '32px', height: '32px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.06)', color: '#888',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,51,51,0.15)'; e.currentTarget.style.color = '#ff3333'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#888'; }}
              >
                <X size={16} />
              </button>
            </div>
            <div style={{ overflowY: 'auto', maxHeight: 'calc(82vh - 90px)', padding: '0 0 2rem' }}>
              {renderToolContent()}
            </div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '40px', background: 'linear-gradient(transparent, rgba(12,12,18,0.95))', pointerEvents: 'none' }} />
          </div>
          <style>{`
            @keyframes toolPanelSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
            @keyframes toolPanelFadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes gradientBorderFlow { 0% { background-position: 0% 50%; } 100% { background-position: 300% 50%; } }
          `}</style>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProducerToolsPanel;
